import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Zap, Target, Award, Users, AlertTriangle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

export function Landing() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="relative z-10 pb-8 bg-white sm:pb-16 md:pb-20 lg:max-w-2xl lg:w-full lg:pb-28 xl:pb-32">
            <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
              <div className="sm:text-center lg:text-left">
                <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                  <span className="block">Land your dream job</span>
                  <span className="block text-blue-600">with AI-powered resumes</span>
                </h1>
                <p className="mt-3 text-base text-gray-500 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
                  Transform your resume for every job application with our AI technology. Get more interviews and stand out from the crowd.
                </p>
                <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start">
                  <div className="rounded-md shadow">
                    <Link
                      to={user ? "/dashboard" : "/signup"}
                      className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 md:py-4 md:text-lg md:px-10"
                    >
                      Get Started Free
                    </Link>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
        <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
          <img
            className="h-56 w-full object-cover sm:h-72 md:h-96 lg:w-full lg:h-full"
            src="https://images.pexels.com/photos/5673488/pexels-photo-5673488.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
            alt="Professional looking at laptop"
          />
        </div>
      </div>

      {/* ATS Warning Section */}
      <div className="bg-blue-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              75% of Resumes Never Reach a Recruiter
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-600 lg:mx-auto">
              Most resumes are rejected by Applicant Tracking Systems (ATS) before a human ever sees them. Don't let your resume be one of them.
            </p>
          </div>
          <div className="mt-10">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
              <div className="bg-white rounded-xl shadow-sm p-6 border border-blue-100">
                <div className="text-4xl font-bold text-blue-600 mb-2">75%</div>
                <p className="text-gray-600">of resumes are rejected by ATS systems before reaching recruiters</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border border-blue-100">
                <div className="text-4xl font-bold text-blue-600 mb-2">98%</div>
                <p className="text-gray-600">of Fortune 500 companies use ATS to screen candidates</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border border-blue-100">
                <div className="text-4xl font-bold text-blue-600 mb-2">3x</div>
                <p className="text-gray-600">higher chance of getting an interview with an ATS-optimized resume</p>
              </div>
            </div>
            <div className="mt-10 text-center">
              <Link
                to={user ? "/dashboard" : "/signup"}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Optimize Your Resume Now <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16 bg-white" id="features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">Features</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Everything you need to succeed
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Our AI-powered platform helps you create the perfect resume for every job application.
            </p>
          </div>

          <div className="mt-16">
            <div className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
              <div className="relative">
                <div className="absolute flex items-center justify-center h-12 w-12 rounded-xl bg-blue-100 text-blue-600">
                  <Zap className="h-6 w-6" />
                </div>
                <p className="ml-16 text-lg leading-6 font-medium text-gray-900">AI-Powered Optimization</p>
                <p className="mt-2 ml-16 text-base text-gray-500">
                  Our AI analyzes job descriptions and optimizes your resume to match the requirements perfectly.
                </p>
              </div>

              <div className="relative">
                <div className="absolute flex items-center justify-center h-12 w-12 rounded-xl bg-blue-100 text-blue-600">
                  <Target className="h-6 w-6" />
                </div>
                <p className="ml-16 text-lg leading-6 font-medium text-gray-900">Targeted Customization</p>
                <p className="mt-2 ml-16 text-base text-gray-500">
                  Customize your resume for each job application with just one click.
                </p>
              </div>

              <div className="relative">
                <div className="absolute flex items-center justify-center h-12 w-12 rounded-xl bg-blue-100 text-blue-600">
                  <Award className="h-6 w-6" />
                </div>
                <p className="ml-16 text-lg leading-6 font-medium text-gray-900">ATS-Friendly Format</p>
                <p className="mt-2 ml-16 text-base text-gray-500">
                  Ensure your resume passes through Applicant Tracking Systems with our optimized formatting.
                </p>
              </div>

              <div className="relative">
                <div className="absolute flex items-center justify-center h-12 w-12 rounded-xl bg-blue-100 text-blue-600">
                  <Users className="h-6 w-6" />
                </div>
                <p className="ml-16 text-lg leading-6 font-medium text-gray-900">Expert Insights</p>
                <p className="mt-2 ml-16 text-base text-gray-500">
                  Get professional recommendations to improve your resume's impact.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
            <span className="block">Ready to boost your career?</span>
            <span className="block">Start using Skillmatch today.</span>
          </h2>
          <p className="mt-4 text-lg leading-6 text-blue-100">
            Join thousands of job seekers who have improved their application success rate.
          </p>
          <Link
            to={user ? "/dashboard" : "/signup"}
            className="mt-8 w-full inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 sm:w-auto"
          >
            Get started for free
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}